import { useCallback, useEffect, useMemo, useState } from 'react'
import { DateTime } from 'luxon'
import { faEdit } from '@fortawesome/pro-regular-svg-icons'
import {
  faEllipsisVertical,
  faUpload,
  faTrash,
} from '@fortawesome/pro-solid-svg-icons'
import AdminUserDocumentsTable from '@/features/user/your-practice/documents/table/AdminUserDocumentsTable'
import { GroupByEnum } from '@/features/user/your-practice/documents/table/helpers'
import { RowComponentProps } from '@/features/user/your-practice/documents/table/TableRows'
import { UserDocument } from '@/features/user/your-practice/documents/lib/userDocuments.slice'
import {
  sortDocumentsByFileNameAsc,
  sortDocumentsByNameAscWithinCreatedAtDesc,
  sortDocumentsByNameAscWithinStatementDateDesc,
} from '@/features/user/your-practice/documents/lib/helpers'
import { Table, Card, Icon, Popover, Button, Select, Link } from '@/ui'
import {
  generateMonthlyStatementOptions,
  UploadDocumentType,
} from '@/constants/businessConstants'
import { compareArrays, documentCategoryOptions } from './helpers'
import DeleteDocumentModal from './DeleteDocumentModal'
import MoveDocumentModal from './MoveFileModal'
import RenameDocumentModal from './RenameDocumentModal'
import { getUserDocumentCategoriesById } from '@/features/admin/user-document-categories/userDocumentCategories.selectors'
import {
  adminUpdateUserDocument,
  adminUpdateStatement,
} from '@/actions/admin/userDocumentsActions'
import { getUserByFinancialProfileId } from '@/selectors/user.selectors'
import { useReselector } from '@/utils/sharedHooks'
import {
  selectAdminOrUserFinancialAccountOptions,
  selectAdminOrUserGroupedFinancialAccountOptions,
} from '@/selectors/financeSelectors'
import { fetchUserFinancialAccounts } from '@/actions/admin/adminFinancialAccountActions'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { GroupedDropdown } from '@/components/GroupedDropdown'
import { User } from '@/reducers/auth/userReducer'
import { useAppDispatch } from '@/utils/typeHelpers'

// TODO: Replace with Checkbox from @/ui, but need to fix the check issue
import { Checkbox } from '@/componentsV1/BaseComponents'

const AdminRowStartCells = ({
  doc,
  onCheck,
}: {
  doc: UserDocument
  onCheck?: (id: number, checked: boolean) => void
}) => {
  const [checked, setChecked] = useState(false)
  return (
    <>
      <Table.Cell>
        <Checkbox
          checked={checked}
          onClick={() => {
            onCheck?.(doc.id, !checked)
            setChecked((checkedBoxes) => !checkedBoxes)
          }}
        />
      </Table.Cell>
      <Table.Cell>
        {DateTime.fromISO(doc.createdAt).toFormat(
          DATE_FORMATS_LUXON.DISPLAY_SHORT
        )}
      </Table.Cell>
      <Table.Cell>
        {doc.signedUrl ? (
          <Link href={doc.signedUrl} newPage>
            {doc.fileDescription}
          </Link>
        ) : (
          doc.fileDescription
        )}
      </Table.Cell>
    </>
  )
}

const AdminRowActions = ({
  documentType,
  doc,
}: {
  documentType: UploadDocumentType
  doc: UserDocument
}) => {
  const [deleteDocumentModalOpen, setDeleteDocumentModalOpen] = useState(false)
  const [moveDocumentModalOpen, setMoveDocumentModalOpen] = useState(false)
  const [renameDocumentModalOpen, setRenameDocumentModalOpen] = useState(false)

  return (
    <Table.Cell>
      <Popover>
        <Popover.Trigger asChild>
          <Button variant="ghost" size="md" iconLeft={faEllipsisVertical} />
        </Popover.Trigger>
        <Popover.Content align="end" className="w-48 p-2">
          <div className="flex flex-col space-y-1">
            {documentType !== UploadDocumentType.RECEIPT && (
              <Button
                variant="ghost"
                className="justify-between w-full"
                onClick={() => setMoveDocumentModalOpen(true)}
              >
                Move File
                <Icon icon={faUpload} />
              </Button>
            )}
            <Button
              variant="ghost"
              className="justify-between w-full"
              onClick={() => setDeleteDocumentModalOpen(true)}
            >
              Delete File
              <Icon icon={faTrash} />
            </Button>
            <Button
              variant="ghost"
              className="justify-between w-full"
              onClick={() => setRenameDocumentModalOpen(true)}
            >
              Rename File
              <Icon icon={faEdit} />
            </Button>
          </div>
        </Popover.Content>
      </Popover>
      <DeleteDocumentModal
        modalOpen={deleteDocumentModalOpen}
        setModalOpen={setDeleteDocumentModalOpen}
        document={doc}
      />
      <MoveDocumentModal
        modalOpen={moveDocumentModalOpen}
        setModalOpen={setMoveDocumentModalOpen}
        document={doc}
        fileType={documentType}
      />
      <RenameDocumentModal
        modalOpen={renameDocumentModalOpen}
        setModalOpen={setRenameDocumentModalOpen}
        document={doc}
      />
    </Table.Cell>
  )
}

const AdminMultiBankStatementRow = ({
  document,
  onCheck,
}: RowComponentProps) => {
  const dispatch = useAppDispatch()
  const user: User | undefined = useReselector(
    getUserByFinancialProfileId,
    document.financialProfileId
  )

  // if Bank Statement modal fetch financial account information when needed
  useEffect(() => {
    if (user?.id) {
      dispatch(fetchUserFinancialAccounts(user.id, true))
    }
  }, [user?.id, dispatch])

  const financialAccountOptions = useReselector(
    selectAdminOrUserGroupedFinancialAccountOptions,
    user?.id
  )

  const handleStatementMonthChange = useCallback(
    (statementMonth?: string) =>
      document.id &&
      dispatch(
        adminUpdateStatement(document.id, {
          statementMonth,
          statement: {
            statementMonth,
          },
        })
      ),
    [dispatch, document]
  )

  const groupedDropdownValue = useMemo(() => {
    if (!document.statement?.statementFinancialAccounts?.length) {
      return []
    }
    return document.statement.statementFinancialAccounts?.map(
      (account) => account.financialAccountId
    )
  }, [document.statement])

  const handleStatementFinancialAccountChange = useCallback(
    (newFinancialAccountIds: (string | number)[]) => {
      if (!document.statement?.id) return
      const statementFinancialAccounts =
        document.statement.statementFinancialAccounts || []
      const statementFinancialAccountIds = statementFinancialAccounts.map(
        (account) => account.financialAccountId
      )
      const { newVals, removedVals } = compareArrays(
        statementFinancialAccountIds,
        newFinancialAccountIds
      )

      /*
        newFinancialAccountIds should all be numbers but this ensures that only numbers 
        are passed to the backend, which expects an array of IDs 
      */
      const accountsToCreate = newVals.filter(
        (val) => typeof val === 'number'
      ) as number[]
      const accountsToDelete = removedVals.filter(
        (val) => typeof val === 'number'
      ) as number[]

      dispatch(
        adminUpdateStatement(document.id, {
          statementFinancialAccounts: {
            accountsToCreate,
            accountsToDelete,
          },
        })
      )
    },
    [dispatch, document]
  )

  return (
    <Table.Row key={document.id}>
      <AdminRowStartCells doc={document} onCheck={onCheck} />
      <Table.Cell className="w-56">
        <Select
          placeholder="Select Statement Month"
          value={document.statement?.statementMonth || undefined}
          options={generateMonthlyStatementOptions()}
          onValueChange={handleStatementMonthChange}
          clearable
        />
      </Table.Cell>
      <Table.Cell className="w-56">
        <GroupedDropdown
          value={groupedDropdownValue}
          onChange={handleStatementFinancialAccountChange}
          options={financialAccountOptions}
          allowOneParent
        />
      </Table.Cell>
      <AdminRowActions
        documentType={UploadDocumentType.STATEMENT}
        doc={document}
      />
    </Table.Row>
  )
}

const AdminBankStatementRow = ({ document, onCheck }: RowComponentProps) => {
  const dispatch = useAppDispatch()
  const user = useReselector(
    getUserByFinancialProfileId,
    document.financialProfileId
  )

  // if Bank Statement modal fetch financial account information when needed
  useEffect(() => {
    if (user?.id) {
      dispatch(fetchUserFinancialAccounts(user.id, true))
    }
  }, [user?.id, dispatch])

  const financialAccountOptions = useReselector(
    selectAdminOrUserFinancialAccountOptions,
    user?.id
  )

  const handleStatementMonthChange = useCallback(
    (statementMonth?: string) =>
      user?.id &&
      dispatch(
        adminUpdateUserDocument(document.id, {
          statementMonth: statementMonth || null,
        })
      ),
    [dispatch, user?.id, document.id]
  )

  const handleStatementFinancialAccountChange = useCallback(
    (statementFinancialAccountId?: number) =>
      user?.id &&
      dispatch(
        adminUpdateUserDocument(document.id, {
          statementFinancialAccountId: statementFinancialAccountId || null,
        })
      ),
    [dispatch, user?.id, document.id]
  )

  return (
    <Table.Row key={document.id}>
      <AdminRowStartCells doc={document} onCheck={onCheck} />
      <Table.Cell>
        <Select
          placeholder="Select Document Category"
          value={document.statementMonth || undefined}
          options={generateMonthlyStatementOptions()}
          onValueChange={handleStatementMonthChange}
          clearable
        />
      </Table.Cell>
      <Table.Cell>
        <Select
          placeholder="Select Financial Account"
          value={document.statementFinancialAccountId || undefined}
          options={financialAccountOptions}
          onValueChange={handleStatementFinancialAccountChange}
          clearable
        />
      </Table.Cell>
      <AdminRowActions
        documentType={UploadDocumentType.STATEMENT}
        doc={document}
      />
    </Table.Row>
  )
}

const AdminReceiptRow = ({ document, onCheck }: RowComponentProps) => (
  <Table.Row key={document.id}>
    <AdminRowStartCells doc={document} onCheck={onCheck} />
    <Table.Cell>
      {document.transactionId ? document.transactionId : null}
    </Table.Cell>
    <AdminRowActions documentType={UploadDocumentType.RECEIPT} doc={document} />
  </Table.Row>
)

const AdminOtherRow = ({ document, onCheck }: RowComponentProps) => (
  <Table.Row key={document.id}>
    <AdminRowStartCells doc={document} onCheck={onCheck} />
    <Table.Cell>{document.customerDescription}</Table.Cell>
    <Table.Cell>{document.needsReview && <Icon icon={faEdit} />}</Table.Cell>
    <AdminRowActions documentType={UploadDocumentType.OTHER} doc={document} />
  </Table.Row>
)

// Document type is needed but both the Tax and Incorporation rows are identical otherwise so use a generator
const makeAdminTaxIncorporationRow =
  (documentType: UploadDocumentType) =>
  ({ document, onCheck }: RowComponentProps) => {
    const dispatch = useAppDispatch()
    const user = useReselector(
      getUserByFinancialProfileId,
      document.financialProfileId
    )
    const documentCategories = useReselector(getUserDocumentCategoriesById)
    const filteredDocumentCategories = useMemo(
      () => documentCategoryOptions(documentCategories, documentType, true),
      [documentCategories]
    )

    const handleDocumentCategoryChange = useCallback(
      (userDocumentCategoryId: number | undefined) =>
        user?.id &&
        dispatch(
          adminUpdateUserDocument(document.id, {
            userDocumentCategoryId:
              userDocumentCategoryId === undefined
                ? null
                : userDocumentCategoryId,
          })
        ),
      [dispatch, document.id, user?.id]
    )

    // Admins can't select archived categories but still show the category if it's already set
    const placeholderText =
      document.userDocumentCategoryId &&
      documentCategories[document.userDocumentCategoryId]?.archivedAt
        ? documentCategories[document.userDocumentCategoryId]?.title
        : 'Select Document Category'

    return (
      <Table.Row key={document.id}>
        <AdminRowStartCells doc={document} onCheck={onCheck} />
        <Table.Cell>
          <Select
            placeholder={placeholderText}
            value={document.userDocumentCategoryId || undefined}
            // the undefined value should be in documentCategoryOptions but a lot of code uses it
            options={[
              { value: undefined, text: '(none)' },
              ...filteredDocumentCategories.map((category) => ({
                value: category.value,
                text: category.text as string,
              })),
            ]}
            onValueChange={handleDocumentCategoryChange}
          />
        </Table.Cell>
        <AdminRowActions documentType={documentType} doc={document} />
      </Table.Row>
    )
  }

const ROW_COMP_MAPPING = {
  statement: AdminBankStatementRow,
  receipt: AdminReceiptRow,
  other: AdminOtherRow,
  tax: makeAdminTaxIncorporationRow(UploadDocumentType.TAX),
  incorporation: makeAdminTaxIncorporationRow(UploadDocumentType.INCORPORATION),
  bookkeeping: makeAdminTaxIncorporationRow(UploadDocumentType.BOOKKEEPING),
  multiStatement: AdminMultiBankStatementRow,
}

const TABLE_OPTIONS = {
  statement: {
    sortFn: sortDocumentsByNameAscWithinStatementDateDesc,
    groupBy: GroupByEnum.StatementYear,
  },
  tax: {
    sortFn: sortDocumentsByNameAscWithinCreatedAtDesc,
    groupBy: GroupByEnum.CategoryYear,
  },
  incorporation: {
    sortFn: sortDocumentsByFileNameAsc,
    groupBy: GroupByEnum.None,
  },
  receipt: {
    sortFn: sortDocumentsByNameAscWithinCreatedAtDesc,
    groupBy: GroupByEnum.MonthWithinYear,
  },
  other: {
    sortFn: sortDocumentsByNameAscWithinCreatedAtDesc,
    groupBy: GroupByEnum.None,
  },
  bookkeeping: {
    sortFn: sortDocumentsByNameAscWithinCreatedAtDesc,
    groupBy: GroupByEnum.CreatedAtYear,
  },
}

const AdminDocumentTableWrapper = ({
  documentType,
  documents,
  onCheck,
}: {
  documentType: UploadDocumentType
  documents: UserDocument[]
  onCheck: (id: number, checked: boolean) => void
}) => {
  const columns = useMemo(() => {
    const tableColumns = [
      { sortBy: null, title: '', width: 2 as const },
      { sortBy: 'createdAt', title: 'Date Uploaded' },
      { sortBy: 'fileName', title: 'File Name' },
    ]

    if (documentType === UploadDocumentType.STATEMENT) {
      tableColumns.push({ sortBy: 'tag', title: 'Statement Month' })
      tableColumns.push({ sortBy: 'account', title: 'Financial Account' })
    } else if (documentType === UploadDocumentType.RECEIPT) {
      tableColumns.push({ sortBy: 'transactionId', title: 'Transaction ID' })
    } else if (documentType === UploadDocumentType.OTHER) {
      tableColumns.push({ sortBy: 'description', title: 'Description' })
      tableColumns.push({ sortBy: 'needsReview', title: 'Needs Review' })
    } else {
      tableColumns.push({ sortBy: 'tag', title: 'Document Category' })
    }

    return [
      ...tableColumns,
      { sortBy: null, title: 'Actions', width: 2 as const },
    ]
  }, [documentType])

  const type =
    documentType === UploadDocumentType.STATEMENT
      ? 'multiStatement'
      : documentType

  return (
    <Card>
      <AdminUserDocumentsTable
        columns={columns}
        documents={documents}
        RowComponent={ROW_COMP_MAPPING[type]}
        tableOptions={TABLE_OPTIONS[documentType]}
        onCheck={onCheck}
        fixed={false}
      />
    </Card>
  )
}

export default AdminDocumentTableWrapper
